import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  BuildingLibraryIcon,
  CreditCardIcon,
  ChartBarIcon,
  PresentationChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  AcademicCapIcon as CertificationIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

const menuItems = [
  { name: 'Tableau de bord', href: '/dashboard', icon: HomeIcon },
  { name: 'Cours', href: '/courses', icon: ClipboardDocumentCheckIcon },
  { name: 'Groupes', href: '/groups', icon: TagIcon },
  { name: 'Étudiants', href: '/students', icon: AcademicCapIcon },
  { name: 'Formateurs', href: '/trainers', icon: UserGroupIcon },
  { name: 'Sessions', href: '/sessions', icon: UserGroupIcon },
  { name: 'Certifications', href: '/certifications', icon: CertificationIcon },
  { name: 'Emploi du temps', href: '/schedule', icon: CalendarDaysIcon },
  { name: 'Salles', href: '/classrooms', icon: BuildingLibraryIcon },
  { name: 'Paiements', href: '/payments', icon: CreditCardIcon },
  { name: 'Bénéfices', href: '/benefits', icon: ChartBarIcon },
  { name: 'Rapports', href: '/reports', icon: PresentationChartBarIcon },
  { name: 'Paramètres', href: '/settings', icon: Cog6ToothIcon },
];

const NavLink = ({ item, pathname, onNavigate }) => {
  const Icon = item.icon;
  const current =
    item.href === '/dashboard' ? pathname === '/dashboard' : pathname.startsWith(item.href);

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
        current
          ? 'bg-forma-600 text-slate-900 dark:text-slate-50 shadow-sm dark:bg-forma-500'
          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-900 dark:text-slate-50'
      }`}
    >
      <Icon
        className={`h-5 w-5 shrink-0 ${
          current ? 'text-slate-900 dark:text-slate-50' : 'text-slate-400 group-hover:text-forma-600 dark:group-hover:text-forma-400'
        }`}
        aria-hidden
      />
      <span className="truncate">{item.name}</span>
    </Link>
  );
};

const Sidebar = ({ isMobile, isDesktopOpen, toggleMobileSidebar }) => {
  const location = useLocation();
  const { pathname } = location;

  return (
    <>
      {/* Desktop */}
      <motion.div
        initial={{ width: isDesktopOpen ? 280 : 0 }}
        animate={{
          width: isDesktopOpen ? 280 : 0,
          opacity: isDesktopOpen ? 1 : 0,
        }}
        className={`fixed bottom-0 left-0 top-0 z-20 hidden overflow-hidden border-r border-slate-200 bg-white lg:block dark:border-slate-800 dark:bg-slate-950`}
        aria-hidden={!isDesktopOpen}
      >
        <div className="flex h-full w-[280px] flex-col pt-14">
          <nav className="hide-scrollbar flex-1 space-y-1 overflow-y-auto px-3 pb-4">
            {menuItems.map((item) => (
              <NavLink key={item.name} item={item} pathname={pathname} />
            ))}
          </nav>
          <div className="border-t border-slate-100 p-3 dark:border-slate-800">
            <Link
              to="/login"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-danger-600 transition hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-950/30"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" aria-hidden />
              Déconnexion
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Mobile */}
      <motion.div
        initial={{ x: '-100%' }}
        animate={{ x: isMobile ? '0%' : '-100%' }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        className={`fixed inset-y-0 left-0 z-50 w-[280px] border-r border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950 lg:hidden`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation principale"
      >
        <div className="flex h-full flex-col">
          <div className="flex h-14 items-center justify-between border-b border-slate-100 px-4 dark:border-slate-800">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Menu</span>
            <button
              type="button"
              onClick={toggleMobileSidebar}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-forma-500 dark:hover:bg-slate-800 dark:text-slate-400"
            >
              <span className="sr-only">Fermer</span>
              <XMarkIcon className="h-6 w-6" aria-hidden />
            </button>
          </div>
          <nav className="hide-scrollbar flex-1 space-y-1 overflow-y-auto p-3">
            {menuItems.map((item) => (
              <NavLink
                key={item.name}
                item={item}
                pathname={pathname}
                onNavigate={toggleMobileSidebar}
              />
            ))}
          </nav>
          <div className="border-t border-slate-100 p-3 dark:border-slate-800">
            <Link
              to="/login"
              onClick={toggleMobileSidebar}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-danger-600 transition hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-950/30"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" aria-hidden />
              Déconnexion
            </Link>
          </div>
        </div>
      </motion.div>

      {isMobile ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] lg:hidden"
          aria-label="Fermer la navigation"
          onClick={toggleMobileSidebar}
        />
      ) : null}
    </>
  );
};

export default Sidebar;
